import { zodResolver } from '@hookform/resolvers/zod'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { FileTypeEnum } from '@repo/toolkit/types'
import UploadFileInput from '@repo/ui/file-input'
import { ImageIcon, UploadIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z, ZodType } from 'zod'

import { getCountries } from '../../../api/countries/get-countries'
import { getFormulas } from '../../../api/formula/get-formulas'
import { getOptions } from '../../../api/options/get-options'
import {
  AccomodationEnum,
  accomodationEnumMapping
} from '../../../lib/types/enums/accomodation-type/accomodation-type'
import {
  StaffPositionEnum,
  staffPositionEnumMapping
} from '../../../lib/types/enums/staff/staff-position.enum'
import {
  StaffQualificationEnum,
  staffQualificationEnumMapping
} from '../../../lib/types/enums/staff/staff-qualifications.enum'
import { UserTypeEnum, userTypeEnumMapping } from '../../../lib/types/enums/user/user-type.enum'
import { GuideInput } from '../../../lib/types/models/guide/guide-input'
import { BaseFormProps, NullableTransformer } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'
import {
  createEnumArraySchema,
  createEnumSchema
} from '../../../lib/utils/validations/enum-validation'
import { FILE_VALIDATION } from '../../../lib/utils/validations/file.validation'
import { createNumberArraySchema } from '../../../lib/utils/validations/int-array-validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledFileInput from '../../elements/inputs/controlled-file-input'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledMultiSelectInput from '../../elements/inputs/controlled-multi-select-input'
import ControlledRadioInput from '../../elements/inputs/controlled-radio-input'
import ValidationButtons from '../../elements/validation-buttons'

const formSchema: ZodType<NullableTransformer<GuideInput>> = z.object({
  userType: createEnumSchema({ fieldName: "Type d'utilisateur", enumType: UserTypeEnum }),
  name: createStringSchema('Nom'),
  description: z.string().optional(),
  picto: FILE_VALIDATION().nullish(),
  pdf: FILE_VALIDATION(),
  countryIds: createNumberArraySchema('Pays').optional(),
  formulaIds: createNumberArraySchema('Formules').optional(),
  accomodationTypes: createEnumArraySchema({
    fieldName: "Types d'hébergement",
    enumType: AccomodationEnum
  }).optional(),
  staffPositions: createEnumArraySchema({
    fieldName: 'Types de postes',
    enumType: StaffPositionEnum
  }).optional(),
  qualifications: createEnumArraySchema({
    fieldName: 'Qualifications',
    enumType: StaffQualificationEnum
  }).optional(),
  optionIds: createNumberArraySchema('Options').optional()
})

const GuideForm = ({
  defaultValues,
  ...validationButtonProps
}: BaseFormProps<GuideInput, true>) => {
  const { t } = useTranslation()

  const form = useForm<GuideInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })

  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: () => getCountries()
  })

  const { data: formulas } = useQuery({
    queryKey: ['formulas'],
    queryFn: () => getFormulas()
  })

  const { data: options } = useQuery({
    queryKey: ['options'],
    queryFn: () => getOptions()
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <InnerBox title="Guide">
          <div className="flex flex-col gap-4">
            <div>
              <Typography
                variant="label-medium"
                color={tailwindColors.black.DEFAULT}
                className="mb-2"
              >
                Catégorie d'utilisateurs concernés *
              </Typography>
              <div className="flex gap-6">
                {Object.values(UserTypeEnum).map((option, index) => (
                  <FormField
                    key={index}
                    control={form.control}
                    name="userType"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <ControlledRadioInput
                            label={userTypeEnumMapping[option].label}
                            checked={field.value === option}
                            onCheck={() => field.onChange(option)}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                ))}
              </div>
            </div>

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledInput
                      {...field}
                      required
                      type="text"
                      label="Nom"
                      placeholder="Host Family Welcome Guide"
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledInput
                      {...field}
                      type="textarea"
                      label="Description"
                      placeholder="Description du guide..."
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="picto"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledFileInput
                      multiple
                      previewOnClick
                      placeholder="Glisser-déposer ou parcourir"
                      types={[FileTypeEnum.IMAGE]}
                      icon={ImageIcon}
                      label={'Déposer des images'}
                      files={field?.value ? [field?.value] : undefined}
                      onFilesChange={(files) => {
                        field.onChange(files.length === 0 ? null : files[0])
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {/* Countries */}
            <FormField
              control={form.control}
              name="countryIds"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledMultiSelectInput
                      {...field}
                      label="Pays"
                      variant="primary"
                      searchable
                      options={
                        countries?.data?.map((country) => ({
                          label: country.name,
                          value: country.id
                        })) ?? []
                      }
                      selectedOptions={
                        field.value?.map((countryId) => ({
                          label:
                            countries?.data?.find((country) => country.id === countryId)?.name ??
                            '',
                          value: countryId
                        })) ?? []
                      }
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="flex items-center gap-3">
              {/* Formulas */}
              <FormField
                control={form.control}
                name="formulaIds"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormControl>
                      <ControlledMultiSelectInput
                        {...field}
                        label="Formule"
                        variant="warning"
                        searchable
                        options={
                          formulas?.data?.map((formula) => ({
                            label: getTranslatedName(formula),
                            value: formula.id
                          })) ?? []
                        }
                        selectedOptions={
                          field.value?.map((formulaId) => ({
                            label: getTranslatedName(
                              formulas?.data?.find((formula) => formula.id === formulaId)
                            ),
                            value: formulaId
                          })) ?? []
                        }
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              {/* Options */}
              <FormField
                control={form.control}
                name="optionIds"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormControl>
                      <ControlledMultiSelectInput
                        {...field}
                        label="Options"
                        variant="primary"
                        searchable
                        options={
                          options?.data?.map((option) => ({
                            label: getTranslatedName(option),
                            value: option.id
                          })) ?? []
                        }
                        selectedOptions={
                          field.value?.map((optionId) => ({
                            label: getTranslatedName(
                              options?.data?.find((option) => option.id === optionId)
                            ),
                            value: optionId
                          })) ?? []
                        }
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            {/* Qualifications */}
            <FormField
              control={form.control}
              name="qualifications"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledMultiSelectInput
                      {...field}
                      label="Qualifications"
                      variant="warning"
                      options={Object.values(StaffQualificationEnum).map((qualification) => ({
                        label: t(staffQualificationEnumMapping[qualification].label),
                        value: qualification
                      }))}
                      selectedOptions={field.value?.map((qualification) => ({
                        label: t(staffQualificationEnumMapping[qualification].label),
                        value: qualification
                      }))}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="flex items-center gap-3">
              {/* Accommodation Types */}
              <FormField
                control={form.control}
                name="accomodationTypes"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormControl>
                      <ControlledMultiSelectInput
                        {...field}
                        label="Type(s) d'hébergements"
                        variant="primary"
                        options={Object.values(AccomodationEnum).map((type) => ({
                          label: accomodationEnumMapping[type],
                          value: type
                        }))}
                        selectedOptions={
                          field.value?.map((type) => ({
                            label: accomodationEnumMapping[type],
                            value: type
                          })) ?? []
                        }
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              {/* Staff Positions */}
              <FormField
                control={form.control}
                name="staffPositions"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormControl>
                      <ControlledMultiSelectInput
                        {...field}
                        label="Type(s) de postes"
                        variant="primary"
                        options={Object.values(StaffPositionEnum).map((value) => ({
                          label: t(staffPositionEnumMapping[value]),
                          value
                        }))}
                        selectedOptions={field.value?.map((position) => ({
                          label: t(staffPositionEnumMapping[position]),
                          value: position
                        }))}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="pdf"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormControl>
                    <UploadFileInput
                      required
                      placeholder="Télécharger le PDF"
                      types={[FileTypeEnum.PDF]}
                      icon={UploadIcon}
                      hasError={!!fieldState.error}
                      label={'Télécharger le PDF'}
                      files={field?.value ? [field?.value] : undefined}
                      onFilesChange={(files) => {
                        field.onChange(files.length === 0 ? null : files[0])
                      }}
                      onFileError={(error) => {
                        if (error) toast.error(error)
                      }}
                      previewOnClick
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </InnerBox>

        <ValidationButtons {...validationButtonProps} hasBorderTop />
      </form>
    </Form>
  )
}

export default GuideForm
